import { useState, useCallback } from "react";
import toast from "react-hot-toast";
import {
  createCalendarEvent,
  createCalendarTask,
  createCalendarReminder,
  updateCalendarEvent,
  getCalendarEvents,
  deleteCalendarEvent,
} from "../api/calender/Calender";
import { CALENDAR_ITEM_TYPES } from "../utils/calendarTransformers";

/**
 * Hook for creating calendar items (events, tasks, reminders)
 * @returns {Object} { createItem, loading, error }
 */
export const useCreateCalendarItem = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createItem = useCallback(async (type, data) => {
    setLoading(true);
    setError(null);

    try {
      let response;

      switch (type) {
        case CALENDAR_ITEM_TYPES.EVENT:
          response = await createCalendarEvent(data);
          break;
        case CALENDAR_ITEM_TYPES.TASK:
          response = await createCalendarTask(data);
          break;
        case CALENDAR_ITEM_TYPES.REMINDER:
          response = await createCalendarReminder(data);
          break;
        default:
          throw new Error(`Unknown calendar item type: ${type}`);
      }

      if (response?.data?.success) {
        toast.success(
          response?.data?.message || "Calendar item created successfully"
        );
        return { success: true, data: response.data };
      } else {
        const errorMessage =
          response?.data?.message || "Failed to create calendar item";
        setError(errorMessage);
        toast.error(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "An error occurred while creating the calendar item";
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  return { createItem, loading, error };
};

/**
 * Hook for updating calendar events
 * @returns {Object} { updateItem, loading, error }
 */
export const useUpdateCalendarItem = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const updateItem = useCallback(async (updatePayload) => {
    setLoading(true);
    setError(null);

    try {
      const response = await updateCalendarEvent(updatePayload);

      if (response?.data?.success) {
        toast.success(
          response?.data?.message || "Calendar item updated successfully"
        );
        return { success: true, data: response.data };
      } else {
        const errorMessage =
          response?.data?.message || "Failed to update calendar item";
        setError(errorMessage);
        toast.error(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "An error occurred while updating the calendar item";
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  return { updateItem, loading, error };
};

/**
 * Hook for fetching calendar events
 * @returns {Object} { events, loading, error, refetch }
 */
export const useFetchCalendarEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchEvents = useCallback(async (fetchParams = {}) => {
    setLoading(true);
    setError(null);

    try {
      const response = await getCalendarEvents(fetchParams);

      // Log the full response structure for debugging
      console.log("📅 Calendar Events API Response:", response);
      console.log("📅 Response.data:", response?.data);
      console.log("📅 Response.data.data:", response?.data?.data);
      console.log("📅 Response.data.data type:", typeof response?.data?.data);
      console.log("📅 Is Array?", Array.isArray(response?.data?.data));

      if (response?.data?.success) {
        // Handle current backend structure:
        // { success, data: { items: [ ...events ] } }
        let eventsData = [];

        if (Array.isArray(response.data?.data?.items)) {
          eventsData = response.data.data.items;
        } else if (Array.isArray(response.data?.data)) {
          eventsData = response.data.data;
        } else if (Array.isArray(response.data?.data?.events)) {
          eventsData = response.data.data.events;
        } else if (Array.isArray(response.data?.data?.data)) {
          eventsData = response.data.data.data;
        }

        console.log("📅 Extracted eventsData:", eventsData);
        console.log("📅 Events count:", eventsData.length);

        setEvents(eventsData);
        return { success: true, data: eventsData };
      } else {
        const errorMessage =
          response?.data?.message || "Failed to fetch calendar events";
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "An error occurred while fetching calendar events";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    events,
    loading,
    error,
    refetch: fetchEvents,
  };
};

/**
 * Hook for deleting calendar events
 * @returns {Object} { deleteItem, loading, error }
 */
export const useDeleteCalendarItem = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const deleteItem = useCallback(async (deletePayload) => {
    setLoading(true);
    setError(null);

    try {
      const response = await deleteCalendarEvent(deletePayload);

      if (response?.data?.success) {
        toast.success(
          response?.data?.message || "Calendar item deleted successfully"
        );
        return { success: true, data: response.data };
      } else {
        const errorMessage =
          response?.data?.message || "Failed to delete calendar item";
        setError(errorMessage);
        toast.error(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "An error occurred while deleting the calendar item";
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteItem, loading, error };
};

